import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CalendarMonth
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

// Dashboard Redesign Components
import DashboardSidebar from './DashboardSidebar';
import DashboardSearch from './DashboardSearch';
import DashboardSection from './DashboardSection';
import PinnedFavoritesSection from './PinnedFavoritesSection';
import ViewToggle from './ViewToggle';
import { useDashboardPreferences } from './useDashboardPreferences';
import {
  getSectionsArray,
  getAllModules,
  calculateSectionBadge,
  ACTION_TO_DIALOG_MAP
} from './dashboardConfig';

// Existing Dialog Components
import InviteMaidDialog from '../../InviteMaidDialog';
import InvitedMaidsDialog from '../../InvitedMaidsDialog';
import AdminEmailTool from '../../AdminEmailTool';
import HCPJobImportManager from '../../admin/HCPJobImportManager';
import SmoobuJobImportManager from '../../admin/SmoobuJobImportManager';
import AutoAssignmentDialog from '../../admin/AutoAssignmentDialog';
import HCPCustomerImportDialog from '../../admin/HCPCustomerImportDialog';
import LeadImportDialog from '../../admin/LeadImportDialog';
import LeadOutreachDialog from '../../admin/LeadOutreachDialog';
import OdooSyncDialog from '../../admin/OdooSyncDialog';
import SquarePaymentsDialog from '../../admin/SquarePaymentsDialog';
import MaidBroadcastDialog from '../../admin/MaidBroadcastDialog';
import ClientOutreachDialog from '../../admin/ClientOutreachDialog';
import ScheduleJobDialog from '../../admin/ScheduleJobDialog';
// SMSTestCard is handled as a special module type in DashboardSection
import PageLayout from '../../PageLayout';

import { questionnaireLeadsAPI } from '../../../services/questionnaireService';

/**
 * Main Dashboard Container - Redesigned with sections, sidebar, and search
 */
const DashboardContainer = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // Preferences hook for localStorage persistence
  const {
    preferences,
    toggleSection,
    expandSections,
    setViewMode,
    togglePinnedModule
  } = useDashboardPreferences();

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Badge counts
  const [badgeCounts, setBadgeCounts] = useState({
    leadsCount: 0,
    invitationsCount: 0
  });

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Dialog states
  const [dialogs, setDialogs] = useState({
    inviteDialogOpen: false,
    invitedMaidsOpen: false,
    emailToolOpen: false,
    hcpImportOpen: false,
    smoobuImportOpen: false,
    autoAssignmentOpen: false,
    scheduleJobOpen: false,
    hcpCustomerImportOpen: false,
    leadImportOpen: false,
    leadOutreachOpen: false,
    maidBroadcastOpen: false,
    clientOutreachOpen: false,
    odooSyncOpen: false,
    squarePaymentsOpen: false
  });

  // Result of the Square OAuth round-trip (/dashboard?square=connected|error&reason=…)
  const [squareResult, setSquareResult] = useState(null);

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

  // Fetch badge counts on mount
  // Back from Square's approval screen: reopen the Square dialog with the outcome
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const square = params.get('square');
    if (!square) return;
    setSquareResult({ status: square, reason: params.get('reason') });
    setDialogs(prev => ({ ...prev, squarePaymentsOpen: true }));
    navigate('/dashboard', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      fetchBadgeCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchBadgeCounts = async () => {
    await Promise.all([
      fetchLeadsCount(),
      fetchInvitationsCount()
    ]);
  };

  const fetchLeadsCount = async () => {
    try {
      const response = await questionnaireLeadsAPI.getLeadsSummary({ days: 7 });
      if (response.success) {
        setBadgeCounts(prev => ({
          ...prev,
          leadsCount: response.data.totalLeads
        }));
      }
    } catch (error) {
      console.error('Error fetching leads count:', error);
    }
  };

  const fetchInvitationsCount = async () => {
    try {
      const response = await fetch(`${apiBase}/api/maids/invitations?limit=1`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setBadgeCounts(prev => ({
          ...prev,
          invitationsCount: data.stats?.total || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching invitations count:', error);
    }
  };

  const handleRefresh = () => {
    setIsLoading(true);
    Promise.all([
      fetchLeadsCount(),
      fetchInvitationsCount()
    ]).finally(() => {
      setIsLoading(false);
    });
  };

  // Dialog handlers
  const openDialog = (dialogKey) => {
    setDialogs(prev => ({ ...prev, [dialogKey]: true }));
  };

  const closeDialog = (dialogKey) => {
    setDialogs(prev => ({ ...prev, [dialogKey]: false }));
  };

  // Handle module click - navigate or open dialog
  const handleModuleClick = useCallback((module) => {
    if (module.action) {
      // Open corresponding dialog
      const dialogKey = ACTION_TO_DIALOG_MAP[module.action];
      if (dialogKey) {
        openDialog(dialogKey);
      }
    } else if (module.externalUrl) {
      // Open external tool (e.g. Odoo accounting) in a new tab
      window.open(module.externalUrl, '_blank', 'noopener,noreferrer');
    } else if (module.path) {
      // Navigate to path
      navigate(module.path);
    } else if (module.component === 'SMSTestCard') {
      // Special case for SMS Test - it's embedded in the section
      // Could scroll to it or handle differently
    }
  }, [navigate]);

  // Handle invitation sent success
  const handleInvitationSent = (invitationData) => {
    setSuccessMessage(`Invitation sent successfully to ${invitationData.firstName} ${invitationData.lastName}!`);
    fetchInvitationsCount();
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  // Filter modules based on search term
  const filterModules = useCallback((modules) => {
    if (!searchTerm.trim()) return modules;

    const term = searchTerm.toLowerCase();
    return modules.filter(module =>
      module.name.toLowerCase().includes(term) ||
      module.description.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  // Get filtered sections with auto-expand
  const getFilteredSections = useCallback(() => {
    const sections = getSectionsArray();
    const filteredSections = sections.map(section => ({
      ...section,
      modules: filterModules(section.modules)
    }));

    // Auto-expand sections with matching modules when searching
    if (searchTerm.trim()) {
      const sectionsWithMatches = filteredSections
        .filter(s => s.modules.length > 0)
        .map(s => s.id);

      if (sectionsWithMatches.length > 0) {
        expandSections(sectionsWithMatches);
      }
    }

    return filteredSections;
  }, [searchTerm, filterModules, expandSections]);

  // Calculate total matching modules for search results
  const getTotalFilteredCount = useCallback(() => {
    const allModules = getAllModules();
    if (!searchTerm.trim()) return allModules.length;
    return filterModules(allModules).length;
  }, [searchTerm, filterModules]);

  // Handle search change
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const filteredSections = getFilteredSections();
  const totalModules = getAllModules().length;
  const filteredCount = getTotalFilteredCount();

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            mb: 3
          }}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Admin Dashboard
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DashboardSearch
              ref={searchRef}
              value={searchTerm}
              onChange={handleSearchChange}
              resultCount={searchTerm ? filteredCount : null}
              totalCount={searchTerm ? totalModules : null}
            />

            <ViewToggle
              mode={preferences.viewMode}
              onToggle={setViewMode}
            />

            <Tooltip title="Go to Service Schedule">
              <IconButton
                onClick={() => navigate('/service-schedule')}
                color="primary"
              >
                <CalendarMonth />
              </IconButton>
            </Tooltip>

            <Tooltip title="Refresh data">
              <IconButton
                onClick={handleRefresh}
                disabled={isLoading}
                color="primary"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Success/Error Messages */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Main Layout with Sidebar */}
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Sidebar Navigation */}
          <DashboardSidebar
            expandedSections={preferences.expandedSections}
            onSectionClick={toggleSection}
            badgeCounts={badgeCounts}
            pinnedCount={preferences.pinnedModules.length}
          />

          {/* Main Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Pinned Favorites Section */}
            <Box id="pinned-favorites">
              <PinnedFavoritesSection
                pinnedModuleIds={preferences.pinnedModules}
                viewMode={preferences.viewMode}
                onModuleClick={handleModuleClick}
                onPinToggle={togglePinnedModule}
                badgeCounts={badgeCounts}
              />
            </Box>

            {/* Dashboard Sections */}
            {filteredSections.map(section => (
              <DashboardSection
                key={section.id}
                id={section.id}
                title={section.title}
                icon={section.icon}
                expanded={preferences.expandedSections.includes(section.id)}
                onToggle={() => toggleSection(section.id)}
                badgeCount={calculateSectionBadge(section, badgeCounts)}
                viewMode={preferences.viewMode}
                modules={section.modules}
                onModuleClick={handleModuleClick}
                pinnedModules={preferences.pinnedModules}
                onPinToggle={togglePinnedModule}
                badgeCounts={badgeCounts}
              />
            ))}

            {/* No results message */}
            {searchTerm && filteredCount === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No modules found matching "{searchTerm}". Try a different search term.
              </Alert>
            )}
          </Box>
        </Box>

        {/* All Dialogs */}
        <InviteMaidDialog
          open={dialogs.inviteDialogOpen}
          onClose={() => closeDialog('inviteDialogOpen')}
          onInvitationSent={handleInvitationSent}
        />

        <InvitedMaidsDialog
          open={dialogs.invitedMaidsOpen}
          onClose={() => closeDialog('invitedMaidsOpen')}
        />

        <AdminEmailTool
          open={dialogs.emailToolOpen}
          onClose={() => closeDialog('emailToolOpen')}
        />

        <HCPJobImportManager
          open={dialogs.hcpImportOpen}
          onClose={() => closeDialog('hcpImportOpen')}
        />

        <SmoobuJobImportManager
          open={dialogs.smoobuImportOpen}
          onClose={() => closeDialog('smoobuImportOpen')}
        />

        <AutoAssignmentDialog
          open={dialogs.autoAssignmentOpen}
          onClose={() => closeDialog('autoAssignmentOpen')}
        />

        <SquarePaymentsDialog
          open={dialogs.squarePaymentsOpen}
          result={squareResult}
          onClose={() => {
            closeDialog('squarePaymentsOpen');
            setSquareResult(null);
          }}
        />
        <OdooSyncDialog
          open={dialogs.odooSyncOpen}
          onClose={() => closeDialog('odooSyncOpen')}
        />

        <HCPCustomerImportDialog
          open={dialogs.hcpCustomerImportOpen}
          onClose={() => closeDialog('hcpCustomerImportOpen')}
        />

        <LeadImportDialog
          open={dialogs.leadImportOpen}
          onClose={() => closeDialog('leadImportOpen')}
        />

        <LeadOutreachDialog
          open={dialogs.leadOutreachOpen}
          onClose={() => closeDialog('leadOutreachOpen')}
        />

        <MaidBroadcastDialog
          open={dialogs.maidBroadcastOpen}
          onClose={() => closeDialog('maidBroadcastOpen')}
        />

        <ClientOutreachDialog
          open={dialogs.clientOutreachOpen}
          onClose={() => closeDialog('clientOutreachOpen')}
        />

        <ScheduleJobDialog
          open={dialogs.scheduleJobOpen}
          onClose={() => closeDialog('scheduleJobOpen')}
          onCreated={(job) => setSuccessMessage(`Job ${job?.jobNumber || ''} scheduled successfully`)}
        />
      </Container>
    </PageLayout>
  );
};

export default DashboardContainer;
